import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError } from '../utils/alert';
import { maskCurrency, parseCurrency, formatInitialAmount } from '../utils/currency';
import QuickCreateModal from './QuickCreateModal';
import './ExpenseForm.css';
import './RevenueDesktop.css';
import './ExpenseDesktop.css';
import './QuickCreateModal.css';
import { toLocalDateInput } from '@sf/shared';

function ExpenseForm({ expense, onSuccess, initialValues }) {
  const [formData, setFormData] = useState(() =>
    expense
      ? { ...expense, amount: formatInitialAmount(expense.amount) }
      : {
          description: '',
          category_id: '',
          amount: '',
          due_date: '',
          payment_date: '',
          accountable_id: '',
          accountable_type: 'App\\Models\\Bank',
          provider_id: initialValues?.provider_id || '',
          payment_method: 'Débito',
          status: 'Paga',
          observations: '',
          attachments: [],
          installments: 1,
          is_fixed: false,
          is_simulation: false,
          total_installments: '',
        }
  );
  const [amountType, setAmountType] = useState('total');
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [providers, setProviders] = useState([]);
  const [showObservations, setShowObservations] = useState(false);
  const [quickCreate, setQuickCreate] = useState(null); // null | 'category' | 'bank' | 'provider'
  const paymentDateRef = useRef(null);
  const width = useWindowWidth();
  const isMobile = width < 768;

  const paymentMethods = [
    { id: 'Débito',        label: 'Débito',   icon: '💳' },
    { id: 'Crédito',       label: 'Crédito',  icon: '💳' },
    { id: 'PIX',           label: 'PIX',      icon: '⚡' },
    { id: 'Boleto',        label: 'Boleto',   icon: '📄' },
    { id: 'Transferência', label: 'TED/DOC',  icon: '🔄' },
    { id: 'Dinheiro',      label: 'Dinheiro', icon: '💵' },
  ];

  const statusOptions = [
    { id: 'Pendente', label: 'Pendente', color: 'warning' },
    { id: 'Paga',     label: 'Paga',     color: 'success' },
    { id: 'Atrasada', label: 'Atrasada', color: 'danger'  },
  ];

  useEffect(() => {
    api.get('/categories?type=Despesa').then(res => setCategories(res.data));
    api.get('/banks').then(res => setBanks(res.data));
    api.get('/credit-cards').then(res => setCreditCards(res.data));
    api.get('/providers').then(res => setProviders(res.data.data));
  }, []);

  useEffect(() => {
    const { payment_method } = formData;
    let newAccountableType = formData.accountable_type;
    let newStatus = formData.status;

    if (payment_method === 'Crédito') {
      newAccountableType = 'App\\Models\\CreditCard';
      newStatus = 'Pendente';
    } else if (payment_method === 'Débito') {
      newAccountableType = 'App\\Models\\Bank';
      newStatus = 'Paga';
    } else if (['Transferência', 'PIX', 'Boleto', 'Dinheiro'].includes(payment_method)) {
      newAccountableType = payment_method === 'Dinheiro' ? null : 'App\\Models\\Bank';
      newStatus = '';
    }

    setFormData(prevData => {
      const typeChanged = newAccountableType !== prevData.accountable_type;
      const statusChanged = newStatus !== prevData.status;

      if (typeChanged || statusChanged) {
        return {
          ...prevData,
          status: newStatus,
          accountable_type: newAccountableType,
          accountable_id: typeChanged ? '' : prevData.accountable_id,
        };
      }
      return prevData;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.payment_method]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'amount') {
      setFormData({ ...formData, amount: maskCurrency(value) });
      return;
    }
    // Ao marcar is_fixed com cartão de crédito, herda o valor de installments
    if (name === 'is_fixed' && checked && formData.payment_method === 'Crédito') {
      setFormData({
        ...formData,
        is_fixed: true,
        total_installments: formData.installments ? String(formData.installments) : '',
      });
      return;
    }
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handlePaymentMethodClick = (method) => {
    setFormData({ ...formData, payment_method: method });
  };

  const handleStatusClick = (status) => {
    if (formData.payment_method !== 'Crédito' && formData.payment_method !== 'Débito') {
      const updates = { status };
      if (status === 'Paga' && !formData.payment_date) {
        updates.payment_date = toLocalDateInput(new Date());
      }
      setFormData({ ...formData, ...updates });
      if (status === 'Paga') {
        setTimeout(() => paymentDateRef.current?.focus(), 80);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const method = expense ? 'put' : 'post';
    const url = expense ? `/expenses/${expense.id}` : '/expenses';

    const qty = parseInt(formData.installments, 10) || 1;
    const rawAmount = parseCurrency(formData.amount);
    const totalAmount = amountType === 'unit' ? rawAmount * qty : rawAmount;

    let dataToSend = { ...formData, amount: totalAmount };
    if (dataToSend.payment_method === 'Dinheiro') {
      dataToSend.accountable_id = null;
      dataToSend.accountable_type = null;
    }

    // Para recorrência de cartão: envia total_installments (null se vazio)
    if (isRecurringCreditCard) {
      dataToSend.total_installments = formData.total_installments
        ? parseInt(formData.total_installments, 10)
        : null;
      // Não envia installments (o backend cria só 1 parcela)
      dataToSend.installments = 1;
    }

    api[method](url, dataToSend)
      .then(() => {
        swalSuccess('Despesa salva com sucesso!');
        onSuccess();
      })
      .catch(error => {
        console.error('Houve um erro ao salvar a despesa!', error);
        swalError('Erro ao salvar despesa.');
      });
  };

  const handleQuickCreated = (type, record) => {
    if (type === 'category') {
      setCategories(prev => [...prev, record]);
      setFormData(prev => ({ ...prev, category_id: String(record.id) }));
    } else if (type === 'bank') {
      setBanks(prev => [...prev, record]);
      setFormData(prev => ({ ...prev, accountable_id: String(record.id) }));
    } else if (type === 'provider') {
      setProviders(prev => [...prev, record]);
      setFormData(prev => ({ ...prev, provider_id: String(record.id) }));
    }
    setQuickCreate(null);
  };

  const showAccountFields = formData.payment_method !== 'Dinheiro';
  const isCreditCard = formData.payment_method === 'Crédito';
  const isStatusDisabled = formData.payment_method === 'Crédito' || formData.payment_method === 'Débito';
  const isRecurringCreditCard = formData.is_fixed && isCreditCard;

  const formatCurrency = (val) =>
    Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const qty = isRecurringCreditCard ? 1 : (parseInt(formData.installments, 10) || 1);
  const rawAmount = parseCurrency(formData.amount);
  const isMultiple = qty > 1 && !expense && !isRecurringCreditCard;

  const previewText = isMultiple && rawAmount > 0
    ? amountType === 'total'
      ? `${qty}× R$ ${formatCurrency(rawAmount / qty)} por parcela`
      : `Total: R$ ${formatCurrency(rawAmount * qty)} · ${qty}× R$ ${formatCurrency(rawAmount)}`
    : null;

  const renderAmountTypeSelector = () => (
    isMultiple ? (
      <div className="amt-toggle-wrap">
        <span className="amt-toggle-label">Este valor é o…</span>
        <div className="amt-toggle-track">
          <div className={`amt-toggle-pill amt-toggle-pill-expense${amountType === 'unit' ? ' slide-unit' : ''}`} />
          <button
            type="button"
            className={`amt-toggle-option${amountType === 'total' ? ' active' : ''}`}
            onClick={() => setAmountType('total')}
          >
            <span className="amt-toggle-icon">÷</span>
            <span className="amt-toggle-text-group">
              <span className="amt-toggle-title">Valor Total</span>
              <span className="amt-toggle-desc">dividido em {qty} parcelas</span>
            </span>
          </button>
          <button
            type="button"
            className={`amt-toggle-option${amountType === 'unit' ? ' active' : ''}`}
            onClick={() => setAmountType('unit')}
          >
            <span className="amt-toggle-icon">∑</span>
            <span className="amt-toggle-text-group">
              <span className="amt-toggle-title">Por Parcela</span>
              <span className="amt-toggle-desc">cada parcela vale este valor</span>
            </span>
          </button>
        </div>
        {previewText && (
          <div className="amt-preview amt-preview-expense">{previewText}</div>
        )}
      </div>
    ) : null
  );

  // ════════════════════════════════════
  // MOBILE FORM — não alterado
  // ════════════════════════════════════
  const renderMobileForm = () => (
    <form onSubmit={handleSubmit} className="expense-form-mobile" autoComplete="off">
      {/* Forma de Pagamento - Grid 3x2 */}
      <div className="form-section">
        <label className="section-label">Forma de Pagamento</label>
        <div className="payment-methods-grid">
          {paymentMethods.map(method => (
            <button
              type="button"
              key={method.id}
              className={`payment-method-btn ${formData.payment_method === method.id ? 'active' : ''}`}
              onClick={() => handlePaymentMethodClick(method.id)}
            >
              <span className="payment-icon">{method.icon}</span>
              <span className="payment-label">{method.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Valor - Campo grande e destacado */}
      <div className="form-section amount-section">
        <label className="section-label">Valor</label>
        <div className="amount-input-wrapper">
          <span className="currency-symbol">R$</span>
          <input
            type="text"
            inputMode="numeric"
            className="amount-input"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="0,00"
            autoComplete="off"
            required
          />
        </div>
      </div>

      {/* Descrição */}
      <div className="form-section">
        <label className="section-label">Descrição</label>
        <input
          type="text"
          className="form-control form-control-lg"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Ex: Conta de luz, Supermercado..."
          autoComplete="off"
          required
        />
      </div>

      {/* Despesa Fixa + Simulação */}
      <div className="form-section">
        <label className="is-fixed-label">
          <input
            type="checkbox"
            name="is_fixed"
            checked={!!formData.is_fixed}
            onChange={handleChange}
            className="is-fixed-checkbox"
          />
          Despesa fixa (recorrente todo mês)
        </label>
        {isRecurringCreditCard && (
          <p style={{ fontSize: '0.78em', color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
            Apenas a parcela do mês atual será lançada. As próximas são geradas automaticamente na data de vencimento.
          </p>
        )}
        <label className="is-simulation-label">
          <input
            type="checkbox"
            name="is_simulation"
            checked={!!formData.is_simulation}
            onChange={handleChange}
            className="is-simulation-checkbox"
          />
          🧪 Lançamento simulado (análise temporária)
        </label>
      </div>

      {/* Data e Parcelas lado a lado */}
      <div className="form-section">
        <div className="row g-2">
          <div className={isRecurringCreditCard ? 'col-12' : 'col-6'}>
            <label className="section-label">
              {isCreditCard ? 'Data da Compra' : 'Vencimento'}
            </label>
            <input
              type="date"
              className="form-control"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              required
            />
          </div>
          {!isRecurringCreditCard && (
            <div className="col-6">
              <label className="section-label">Parcelas</label>
              <input
                type="number"
                className="form-control"
                name="installments"
                value={formData.installments}
                onChange={handleChange}
                min="1"
                disabled={!!expense}
              />
            </div>
          )}
        </div>
      </div>

      {/* Total de parcelas para recorrência de cartão */}
      {isRecurringCreditCard && !expense && (
        <div className="form-section">
          <label className="section-label">
            Total de parcelas
            <span style={{ fontSize: '0.8em', color: '#6b7280', marginLeft: 6 }}>
              (vazio = recorrência indefinida)
            </span>
          </label>
          <input
            type="number"
            className="form-control"
            name="total_installments"
            value={formData.total_installments}
            onChange={handleChange}
            min="1"
            placeholder="Ex: 12"
          />
        </div>
      )}

      {/* Valor Total vs Unitário */}
      {isMultiple && (
        <div className="form-section">
          {renderAmountTypeSelector()}
        </div>
      )}

      {/* Categoria */}
      <div className="form-section">
        <div className="qc-label-row">
          <label className="section-label">Categoria</label>
          <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('category')}>+ Nova</button>
        </div>
        <select
          className="form-select form-select-lg"
          name="category_id"
          value={formData.category_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar categoria</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      </div>

      {/* Conta/Cartão */}
      {showAccountFields && (
        <div className="form-section">
          <div className="qc-label-row">
            <label className="section-label">
              {isCreditCard ? 'Cartão de Crédito' : 'Conta Bancária'}
            </label>
            {!isCreditCard && (
              <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('bank')}>+ Nova</button>
            )}
          </div>
          <select
            className="form-select form-select-lg"
            name="accountable_id"
            value={formData.accountable_id}
            onChange={handleChange}
            required
          >
            <option value="">Selecionar</option>
            {isCreditCard
              ? creditCards.map(card => (
                  <option key={card.id} value={card.id}>
                    {card.name} •••• {card.last_four_digits}
                  </option>
                ))
              : banks.map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name}
                  </option>
                ))}
          </select>
        </div>
      )}

      {/* Status - Botões */}
      <div className="form-section">
        <label className="section-label">Status</label>
        <div className="status-buttons">
          {statusOptions.map(status => (
            <button
              type="button"
              key={status.id}
              className={`status-btn ${status.color} ${formData.status === status.id ? 'active' : ''}`}
              onClick={() => handleStatusClick(status.id)}
              disabled={isStatusDisabled}
            >
              {status.label}
            </button>
          ))}
        </div>
        {isStatusDisabled && (
          <small className="text-muted">Status definido automaticamente</small>
        )}
      </div>

      {/* Data de Pagamento (não crédito) */}
      {!isCreditCard && (
        <div className={`form-section${formData.status === 'Paga' ? ' payment-date-highlight-ex' : ''}`}>
          <label className="section-label">
            Data de Pagamento{formData.status !== 'Paga' ? ' (Opcional)' : ''}
          </label>
          <input
            ref={paymentDateRef}
            type="date"
            className="form-control"
            name="payment_date"
            value={formData.payment_date}
            onChange={handleChange}
          />
        </div>
      )}

      {/* Fornecedor */}
      <div className="form-section">
        <div className="qc-label-row">
          <label className="section-label">Fornecedor (Opcional)</label>
          <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('provider')}>+ Novo</button>
        </div>
        <select
          className="form-select"
          name="provider_id"
          value={formData.provider_id}
          onChange={handleChange}
        >
          <option value="">Nenhum</option>
          {providers.map(provider => (
            <option key={provider.id} value={provider.id}>{provider.name}</option>
          ))}
        </select>
      </div>

      {/* Observações - Expansível */}
      <div className="form-section">
        <button
          type="button"
          className="expand-btn"
          onClick={() => setShowObservations(!showObservations)}
        >
          <span>Observações</span>
          <span className="expand-icon">{showObservations ? '−' : '+'}</span>
        </button>
        {showObservations && (
          <textarea
            className="form-control mt-2"
            name="observations"
            value={formData.observations}
            onChange={handleChange}
            rows="3"
            placeholder="Adicione observações..."
          />
        )}
      </div>

      {/* Botão Salvar Fixo */}
      <div className="mobile-form-footer">
        <button type="submit" className="btn btn-primary btn-lg w-100">
          Salvar Despesa
        </button>
      </div>
    </form>
  );

  // ════════════════════════════════════
  // DESKTOP FORM — redesenhado
  // ════════════════════════════════════
  const renderDesktopForm = () => (
    <form onSubmit={handleSubmit} className="rv-form-desktop">

      {/* Forma de pagamento */}
      <div className="rv-form-field-full">
        <label className="rv-form-label">Forma de Pagamento</label>
        <div className="rv-payment-grid">
          {paymentMethods.map(method => (
            <button
              type="button"
              key={method.id}
              className={`rv-payment-btn ${formData.payment_method === method.id ? 'active' : ''}`}
              onClick={() => handlePaymentMethodClick(method.id)}
            >
              <span className="rv-pay-icon">{method.icon}</span>
              <span>{method.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Descrição + Status */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="description">Descrição</label>
          <input
            type="text"
            className="rv-form-input"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Ex: Conta de luz, Supermercado..."
            required
          />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label">
            Status
            {isStatusDisabled && <span className="ex-status-note">— definido automaticamente</span>}
          </label>
          <div className="rv-status-group">
            {statusOptions.map(s => (
              <button
                type="button"
                key={s.id}
                className={`rv-status-toggle ${formData.status === s.id ? `active-${s.color}` : ''}`}
                onClick={() => handleStatusClick(s.id)}
                disabled={isStatusDisabled}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Despesa Fixa + Simulação */}
      <div className="rv-form-field-full sim-flags-row">
        <div>
          <label className="is-fixed-label">
            <input
              type="checkbox"
              name="is_fixed"
              checked={!!formData.is_fixed}
              onChange={handleChange}
              className="is-fixed-checkbox"
            />
            Despesa fixa (recorrente todo mês)
          </label>
          {isRecurringCreditCard && (
            <p style={{ fontSize: '0.78em', color: '#6b7280', marginTop: 3, marginBottom: 0 }}>
              Apenas a parcela do mês atual será lançada. As próximas são geradas automaticamente na data de vencimento.
            </p>
          )}
        </div>
        <label className="is-simulation-label">
          <input
            type="checkbox"
            name="is_simulation"
            checked={!!formData.is_simulation}
            onChange={handleChange}
            className="is-simulation-checkbox"
          />
          🧪 Lançamento simulado
        </label>
      </div>

      {/* Valor + Data + Parcelas */}
      <div className="rv-form-grid-3">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="amount">Valor (R$)</label>
          <input
            type="text"
            inputMode="numeric"
            className="rv-form-input"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
          />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="due_date">
            {isCreditCard ? 'Data da Compra' : 'Vencimento'}
          </label>
          <input
            type="date"
            className="rv-form-input"
            id="due_date"
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            required
          />
        </div>
        {isRecurringCreditCard && !expense ? (
          <div className="rv-form-field">
            <label className="rv-form-label" htmlFor="total_installments">
              Total de parcelas
              <span style={{ fontSize: '0.78em', color: '#6b7280', marginLeft: 6 }}>
                (vazio = indefinido)
              </span>
            </label>
            <input
              type="number"
              className="rv-form-input"
              id="total_installments"
              name="total_installments"
              value={formData.total_installments}
              onChange={handleChange}
              min="1"
              placeholder="Ex: 12"
            />
          </div>
        ) : (
          <div className="rv-form-field">
            <label className="rv-form-label" htmlFor="installments">Parcelas</label>
            <input
              type="number"
              className="rv-form-input"
              id="installments"
              name="installments"
              value={formData.installments}
              onChange={handleChange}
              min="1"
              disabled={!!expense}
            />
          </div>
        )}
      </div>

      {/* Valor Total vs Unitário */}
      {isMultiple && (
        <div className="rv-form-field-full">
          {renderAmountTypeSelector()}
        </div>
      )}

      {/* Categoria + Conta/Cartão */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <div className="qc-label-row">
            <label className="rv-form-label" htmlFor="category_id">Categoria</label>
            <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('category')}>+ Nova</button>
          </div>
          <select className="rv-form-select" id="category_id" name="category_id" value={formData.category_id} onChange={handleChange} required>
            <option value="">Selecionar Categoria</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {showAccountFields ? (
          <div className="rv-form-field">
            <div className="qc-label-row">
              <label className="rv-form-label" htmlFor="accountable_id">
                {isCreditCard ? 'Cartão de Crédito' : 'Conta Bancária'}
              </label>
              {!isCreditCard && (
                <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('bank')}>+ Nova</button>
              )}
            </div>
            <select className="rv-form-select" id="accountable_id" name="accountable_id" value={formData.accountable_id} onChange={handleChange} required>
              <option value="">Selecionar</option>
              {isCreditCard
                ? creditCards.map(card => <option key={card.id} value={card.id}>{card.name} •••• {card.last_four_digits}</option>)
                : banks.map(bank => <option key={bank.id} value={bank.id}>{bank.name} - {bank.account_number}</option>)}
            </select>
          </div>
        ) : <div></div>}
      </div>

      {/* Fornecedor + Data de Pagamento */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <div className="qc-label-row">
            <label className="rv-form-label" htmlFor="provider_id">Fornecedor (Opcional)</label>
            <button type="button" className="qc-add-btn" onClick={() => setQuickCreate('provider')}>+ Novo</button>
          </div>
          <select className="rv-form-select" id="provider_id" name="provider_id" value={formData.provider_id} onChange={handleChange}>
            <option value="">Nenhum</option>
            {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {!isCreditCard && (
          <div className={`rv-form-field${formData.status === 'Paga' ? ' payment-date-highlight-ex' : ''}`}>
            <label className="rv-form-label" htmlFor="payment_date">
              Data de Pagamento{formData.status !== 'Paga' ? ' (Opcional)' : ''}
            </label>
            <input
              ref={paymentDateRef}
              type="date"
              className="rv-form-input"
              id="payment_date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
            />
          </div>
        )}
      </div>

      {/* Observações */}
      <div className="rv-form-field-full">
        <label className="rv-form-label" htmlFor="observations">Observações (Opcional)</label>
        <textarea
          className="rv-form-textarea"
          id="observations"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          placeholder="Adicione observações..."
        />
      </div>

      {/* Salvar */}
      <div className="rv-form-footer">
        <button type="submit" className="ex-btn-submit">Salvar Despesa</button>
      </div>
    </form>
  );

  return (
    <>
      {isMobile ? renderMobileForm() : renderDesktopForm()}
      {quickCreate && (
        <QuickCreateModal
          type={quickCreate}
          context="Despesa"
          onCreated={(record) => handleQuickCreated(quickCreate, record)}
          onClose={() => setQuickCreate(null)}
        />
      )}
    </>
  );
}

export default ExpenseForm;
